import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import styled from '@emotion/styled'
import {PageTitle} from '../../../_metronic/layout/core'
import * as API from '../../api'
import PreloaderIcon from '../../components/PreloaderIcon'
import BtnHelp from '../../components/BtnHelp'
import ManualModal from '../utils/ManualModal'
import ViewerModal from '../utils/ViewerModal'
import {isSuperAdmin} from '../../modules/auth'
import DownloadModal from './DownloadModal'
import LojasRelacionamentoFilterModal from './LojasRelacionamentoFilterModal'
import {
  formatLojasValue,
  LOJAS_COLUMN_LABELS,
  LOJAS_RELACIONAMENTO_TITLES,
  LojasRelacionamentoFilters,
  LojasRelacionamentoType,
} from './lojasRelacionamento'

const Style = styled.div`
  .scroll-container {
    max-height: calc(100vh - 250px);
    overflow-y: auto;
  }

  .table-row-bordered thead th {
    background-color: white;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
  }

  .table-row-bordered tbody tr:nth-of-type(even) {
    background-color: #f6f6f6;
  }

  .table-row-bordered tbody tr:nth-of-type(odd) {
    background-color: white;
  }

  .table-row-bordered tbody tr:hover {
    background-color: #e2e6ea;
  }

  .table-row-bordered td,
  .table-row-bordered th {
    padding: 0.5rem;
  }

  .table-row-bordered th:first-of-type,
  .table-row-bordered td:first-of-type {
    padding-left: 1rem;
  }

  .card-title .card-label {
    color: #7e8299;
    margin-bottom: 0.5rem;
  }

  .missing svg {
    color: #f35050;
  }
`

type Props = {type: LojasRelacionamentoType}

const LojasRelacionamentoReport: FC<Props> = ({type}) => {
  const [filters, setFilters] = useState<LojasRelacionamentoFilters | null>(null)
  const [limit, setLimit] = useState(100)
  const [columns, setColumns] = useState<string[]>([])
  const [rows, setRows] = useState<unknown[][] | null>(null)
  const [counter, setCounter] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [filterModal, setFilterModal] = useState(true)
  const [downloadModal, setDownloadModal] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const [jobId, setJobId] = useState<string | undefined>()
  const [manual, setManual] = useState<{path: string} | null>(null)
  const [manualModal, setManualModal] = useState(false)
  const [viewerModal, setViewerModal] = useState(false)
  const superAdmin = isSuperAdmin()
  const requestId = useRef(0)
  const title = LOJAS_RELACIONAMENTO_TITLES[type]

  useEffect(() => {
    if (manualModal) return
    let active = true
    API.getManualReport(type)
      .then((response) => {
        if (active) setManual(response?.success ? response.data?.[0] ?? null : null)
      })
      .catch(() => {
        if (active) setManual(null)
      })
    return () => {
      active = false
    }
  }, [type, manualModal])

  const handleHelpClick = () => {
    if (superAdmin) setManualModal(true)
    else if (manual) setViewerModal(true)
  }

  const loadData = useCallback(async () => {
    if (!filters) return
    const currentRequest = ++requestId.current
    setLoading(true)
    setError(false)
    setRows(null)
    try {
      const response = await API.getLojasRelacionamentoReport(filters, type, false, limit)
      if (!response?.success || !Array.isArray(response.columns) || !Array.isArray(response.data)) {
        throw new Error('Falha ao carregar relatório')
      }
      if (currentRequest !== requestId.current) return
      setColumns(response.columns)
      setRows(response.data)
      setCounter(Number(response.counter) || 0)
    } catch (_) {
      if (currentRequest === requestId.current) setError(true)
    } finally {
      if (currentRequest === requestId.current) setLoading(false)
    }
  }, [filters, type, limit])

  useEffect(() => {
    loadData()
    return () => {
      requestId.current += 1
    }
  }, [loadData])

  const exportXLSX = async () => {
    if (!filters || downloadModal) return
    setJobId(undefined)
    setDownloadError(false)
    setDownloadModal(true)
    if (counter > 2000000) return
    try {
      const response = await API.getLojasRelacionamentoReport(filters, type, true, limit)
      if (!response?.success || !response.data) throw new Error('Falha ao exportar relatório')
      setJobId(response.data)
    } catch (_) {
      setDownloadError(true)
    }
  }

  return (
    <Style>
      <PageTitle>{title}</PageTitle>
      <Modal show={filterModal} onHide={() => setFilterModal(false)} centered size='lg'>
        {filterModal && (
          <LojasRelacionamentoFilterModal
            type={type}
            data={filters}
            onChange={(nextFilters) => {
              setFilters({...nextFilters})
              setFilterModal(false)
            }}
            onClose={() => setFilterModal(false)}
          />
        )}
      </Modal>
      <Modal show={downloadModal} centered dialogClassName='mw-500px'>
        {downloadModal && (
          <DownloadModal
            url=''
            jobId={jobId}
            error={downloadError}
            counter={counter}
            onClose={() => setDownloadModal(false)}
          />
        )}
      </Modal>
      <Modal
        id='manual_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-800px'
        show={manualModal}
        onHide={() => setManualModal(false)}
      >
        {manualModal && (
          <ManualModal onClose={() => setManualModal(false)} type={type} item={manual} />
        )}
      </Modal>
      <Modal
        id='viewer_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-1000px'
        show={viewerModal}
        onHide={() => setViewerModal(false)}
      >
        {viewerModal && manual && (
          <ViewerModal onClose={() => setViewerModal(false)} path={manual.path} />
        )}
      </Modal>
      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Relatório de {title}</span>
          </h3>
          {rows && !error && !loading && (
            <>
              <span className='mt-4 mb-1' style={{color: '#7E8299'}} aria-live='polite'>
                Total de resultados: <strong>{counter.toLocaleString('pt-BR')}</strong>
              </span>
              <span className='mt-4 mb-1' style={{color: '#7E8299'}}>
                Resultados visíveis: <strong>{rows.length.toLocaleString('pt-BR')}</strong>
              </span>
            </>
          )}
          <div className='card-toolbar'>
            <label htmlFor={`${type}-limit`}>Limite:</label>
            <select
              id={`${type}-limit`}
              className='form-select form-select-sm w-100px form-select-solid ms-5 me-5'
              value={limit}
              disabled={loading}
              onChange={(event) => setLimit(Number(event.target.value))}
            >
              {[100, 250, 500, 1000].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <button
              type='button'
              className='btn btn-sm btn-light-primary me-4'
              onClick={() => setFilterModal(true)}
            >
              Filtros
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={exportXLSX}
              disabled={loading || !rows?.length || downloadModal || error}
            >
              Exportar XLSX
            </button>
            {(superAdmin || manual) && (
              <div className={`help-button ${manual ? '' : 'missing'}`} title='Manual do relatório'>
                <BtnHelp onClick={handleHelpClick} />
              </div>
            )}
          </div>
        </div>
        <div className='card-body py-3'>
          {loading && (
            <div className='text-center py-10' role='status'>
              <p>Carregando, aguarde um momento</p>
              <div className='d-flex justify-content-center'>
                <PreloaderIcon />
              </div>
            </div>
          )}
          {error && (
            <div className='alert alert-danger' role='alert'>
              Não foi possível carregar o relatório. Tente novamente.
              <button type='button' className='btn btn-sm btn-light-danger ms-3' onClick={loadData}>
                Tentar novamente
              </button>
            </div>
          )}
          {!filters && !loading && (
            <p className='text-muted text-center py-10'>
              Selecione os filtros para consultar o relatório.
            </p>
          )}
          {rows?.length === 0 && !loading && !error && (
            <p className='text-muted text-center py-10'>Nenhum resultado encontrado.</p>
          )}
          {!!rows?.length && !loading && !error && (
            <div className='table-responsive scroll-container'>
              <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                <caption className='visually-hidden'>{title}</caption>
                <thead>
                  <tr className='fw-bold'>
                    {columns.map((column) => (
                      <th key={column} scope='col' className='min-w-150px'>
                        {LOJAS_COLUMN_LABELS[column] || column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns.map((column, columnIndex) => (
                        <td key={column}>
                          <div className='form-check form-check-sm form-check-custom form-check-solid'>
                            <span className='text-muted fw-semibold d-block fs-7'>
                              {formatLojasValue(column, row[columnIndex])}
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Style>
  )
}

export default LojasRelacionamentoReport
